/**
 * IntakeLinkDetailPage — page détail broker `/intake-links/:id` (Chunk 9.N.3.B).
 *
 * Sections :
 *   1. Breadcrumb « Liens d'intake / [titre projet] » (titre projet = lien
 *      vers /projects/:project_id si project non-null, sinon « Projet supprimé »).
 *   2. Header : titre projet (H1), status badge, dates créé/expire, count
 *      submissions, bouton « Révoquer » réutilisant `RevokeIntakeLinkDialog`
 *      9.N.3.A (décision PO §6).
 *   3. Section « Soumissions reçues » avec listing paginé `useIntakeEventsByLink`
 *      → cartes empilées `IntakeEventCard` (collapsed/expanded inline).
 *
 * États rendus distincts (CLAUDE.md §3 « states never shared ») :
 *   - loading       — spinner inline
 *   - not_found     — ApiException 404 → bandeau « Lien introuvable » + bouton retour
 *   - error         — autre ApiException → bandeau bilingue
 *   - empty (events) — lien OK + 0 events → sub-state « Aucune soumission »
 *   - filled        — lien OK + ≥ 1 event → cartes empilées
 *
 * Sécurité (CRITIQUE) :
 *   - Le DOM rendu ne contient JAMAIS `intake_token` ni `intake_url` (parité
 *     anti-leak GET /:id 9.N.3.B.0). Test RTL vérifie 0 occurrence.
 *   - Aucun `console.log` du payload event, file_name, signed URL nulle part.
 *   - Le bouton Révoquer utilise `RevokeIntakeLinkDialog` 9.N.3.A qui invalide
 *     `["intake-links"]` côté TanStack — la page détail re-fetchera le lien
 *     automatiquement (status devient "revoked", bouton disabled).
 */

import type { IntakeLinkListItem } from "@kollectivia/shared";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Pagination, usePagination } from "../../components/Pagination";
import { api, ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import {
  buildEventsByLinkCsvPath,
  useIntakeEventsByLink,
  useIntakeLink,
} from "../../lib/queries/intakeLinks";

import { IntakeEventCard } from "./_parts/IntakeEventCard";
import {
  EMPTY_FILTERS,
  hasActiveFilter,
  IntakeEventsFilters,
  useDebouncedSearch,
  type EventsFilterState,
} from "./_parts/IntakeEventsFilters";
import { IntakeLinkStatusBadge } from "./_parts/IntakeLinkStatusBadge";
import { RevokeIntakeLinkDialog } from "./_parts/RevokeIntakeLinkDialog";

const EVENTS_LIMIT = 20;

export function IntakeLinkDetailPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const link = useIntakeLink(id);
  const pagination = usePagination();

  // 9.N.3.C — état des filtres (Q5=A : aucun filtre par défaut au mount)
  const [filters, setFilters] = useState<EventsFilterState>(EMPTY_FILTERS);
  const debouncedQ = useDebouncedSearch(filters.q, 300);

  // Reset pagination quand un filtre change (sinon on garde un cursor obsolète).
  useEffect(() => {
    pagination.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.from, filters.to, debouncedQ]);

  const events = useIntakeEventsByLink({
    linkId: id,
    cursor: pagination.cursor,
    limit: EVENTS_LIMIT,
    status: filters.status,
    from: filters.from ? `${filters.from}T00:00:00.000Z` : undefined,
    to: filters.to ? `${filters.to}T23:59:59.999Z` : undefined,
    q: debouncedQ || undefined,
  });

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [exportInFlight, setExportInFlight] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportTruncatedWarning, setExportTruncatedWarning] = useState(false);
  const eventsListId = useId();

  const handleExport = async () => {
    if (!id) return;
    setExportError(null);
    setExportTruncatedWarning(false);
    setExportInFlight(true);
    try {
      const path = buildEventsByLinkCsvPath({
        linkId: id,
        status: filters.status,
        from: filters.from ? `${filters.from}T00:00:00.000Z` : undefined,
        to: filters.to ? `${filters.to}T23:59:59.999Z` : undefined,
        q: debouncedQ || undefined,
      });
      const isoDate = new Date().toISOString().slice(0, 10);
      const filename = `intake-events-${id.slice(0, 8)}-${isoDate}.csv`;
      const result = await api.download(path, filename);
      if (result.truncated) setExportTruncatedWarning(true);
    } catch (err) {
      const msg =
        err instanceof ApiException
          ? getApiErrorMessage(err, lang) || t("intake_links.detail.events.filters.export_failed")
          : t("intake_links.detail.events.filters.export_failed");
      setExportError(msg);
    } finally {
      setExportInFlight(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  if (link.isLoading && !link.data) {
    return (
      <div className="mx-auto max-w-4xl">
        <Breadcrumb projectTitle={null} projectId={null} />
        <LoadingState />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Not found / error states
  // ---------------------------------------------------------------------------
  if (link.error) {
    const isNotFound = link.error instanceof ApiException && link.error.status === 404;
    if (isNotFound) {
      return (
        <div className="mx-auto max-w-4xl">
          <Breadcrumb projectTitle={null} projectId={null} />
          <NotFoundState onBack={() => navigate("/intake-links")} />
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-4xl">
        <Breadcrumb projectTitle={null} projectId={null} />
        <div
          role="alert"
          className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {link.error instanceof ApiException
            ? getApiErrorMessage(link.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Filled — link.data présent. Garde-fou état transitoire : si isLoading=false
  // ET error=undefined ET data=undefined (peu probable mais possible avec
  // certaines configs TanStack Query / mocks de test), on retombe sur loading
  // plutôt que de planter sur `data.project`.
  // ---------------------------------------------------------------------------
  if (!link.data) {
    return (
      <div className="mx-auto max-w-4xl">
        <Breadcrumb projectTitle={null} projectId={null} />
        <LoadingState />
      </div>
    );
  }
  const data: IntakeLinkListItem = link.data;
  const projectTitle = data.project?.title ?? null;
  const projectId = data.project?.id ?? null;

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumb projectTitle={projectTitle} projectId={projectId} />

      {/* Header */}
      <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            {projectTitle ? (
              projectId ? (
                <Link
                  to={`/projects/${projectId}`}
                  className="focus-visible:ring-brand-500 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  {projectTitle}
                </Link>
              ) : (
                projectTitle
              )
            ) : (
              <span className="text-slate-400">{t("intake_links.detail.project_deleted")}</span>
            )}
          </h1>
          <p className="mt-2 text-sm text-slate-700">
            {data.form_template_version_id === null
              ? t("intake_links.detail.form_classic")
              : data.form
                ? t("intake_links.detail.form_version", {
                    name: data.form.name,
                    number: data.form.version_number,
                  })
                : t("intake_links.detail.form_unavailable")}
          </p>
          <p className="mt-2 text-sm text-slate-700">
            {t(data.form?.supports_client_signature ? "flow_clarity.mandate" : "flow_clarity.form")}{" "}
            · {data.contact?.name ?? t("flow_clarity.contact_unknown")}
          </p>
          <p className="mt-2 text-sm text-slate-700">
            {t(
              `flow_clarity.invitation_${data.invitation_status === undefined ? "unknown" : (data.invitation_status ?? "prepared")}`,
            )}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            <IntakeLinkStatusBadge status={data.status} />
            <span className="tabular-nums">
              {t("intake_links.detail.header_meta_created", {
                date: formatDateShort(data.created_at, lang),
              })}
            </span>
            <span className="tabular-nums">
              {data.expires_at
                ? t("intake_links.detail.header_meta_expires", {
                    date: formatDateShort(data.expires_at, lang),
                  })
                : t("intake_links.detail.header_meta_expires_never")}
            </span>
            <span className="tabular-nums">
              {data.max_submissions === null
                ? t("intake_links.detail.header_meta_submissions_unlimited", {
                    count: data.submission_count,
                  })
                : t("intake_links.detail.header_meta_submissions", {
                    count: data.submission_count,
                    max: data.max_submissions,
                  })}
            </span>
          </div>
        </div>
        <button
          type="button"
          disabled={data.status === "revoked"}
          onClick={() => setRevokeOpen(true)}
          className="touch-manipulation rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:hover:bg-slate-50"
        >
          {t("intake_links.list.revoke")}
        </button>
      </header>

      {/* Section Soumissions */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-slate-900">
          {t("intake_links.detail.events.title")}
        </h2>

        {/* Filter panel + export CSV (9.N.3.C) */}
        <div className="mt-4">
          <IntakeEventsFilters
            value={filters}
            onChange={setFilters}
            onExport={handleExport}
            exportDisabled={
              exportInFlight || events.isLoading || (events.data?.items.length ?? 0) === 0
            }
            controlsId={eventsListId}
          />
          {/* Warning truncate (≥ 5000 rows export) */}
          {exportTruncatedWarning && (
            <div
              role="status"
              className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            >
              {t("intake_links.detail.events.filters.export_truncated_warning")}
            </div>
          )}
          {/* Erreur export (ApiException) */}
          {exportError && (
            <div
              role="alert"
              className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {exportError}
            </div>
          )}
        </div>

        <div id={eventsListId} className="mt-4">
          {events.isLoading && !events.data ? (
            <div className="text-sm text-slate-400">{t("intake_links.detail.events.loading")}</div>
          ) : events.error ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {events.error instanceof ApiException
                ? getApiErrorMessage(events.error, lang) || t("errors.generic")
                : t("errors.generic")}
            </div>
          ) : (events.data?.items.length ?? 0) === 0 ? (
            // Empty states distincts (R-9N3C-7) :
            //   - filtre actif → no_match (amber, suggère reset)
            //   - aucun filtre → empty (slate, première soumission attendue)
            hasActiveFilter(filters) ? (
              <NoMatchEventsState onReset={() => setFilters(EMPTY_FILTERS)} />
            ) : (
              <EmptyEventsState />
            )
          ) : (
            <>
              <ul className="space-y-3">
                {events.data!.items.map((event) => (
                  <li key={event.id}>
                    <IntakeEventCard event={event} linkId={data.id} />
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                <Pagination
                  pageNumber={pagination.pageNumber}
                  hasPrev={pagination.hasPrev}
                  hasNext={!!events.data?.next_cursor}
                  onPrev={pagination.prev}
                  onNext={() => pagination.next(events.data?.next_cursor ?? null)}
                  disabled={events.isFetching}
                />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Dialog Révoquer (réutilisé 9.N.3.A) */}
      <RevokeIntakeLinkDialog
        open={revokeOpen}
        intakeLinkId={data.id}
        projectTitle={projectTitle}
        onClose={() => setRevokeOpen(false)}
        onRevoked={() => setRevokeOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-composants
// ---------------------------------------------------------------------------

function Breadcrumb({
  projectTitle,
  projectId,
}: {
  projectTitle: string | null;
  projectId: string | null;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label="breadcrumb" className="text-sm">
      <ol className="flex flex-wrap items-center gap-1.5 text-slate-500">
        <li>
          <Link
            to="/intake-links"
            className="focus-visible:ring-brand-500 rounded-sm hover:text-slate-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("intake_links.detail.breadcrumb_root")}
          </Link>
        </li>
        <li aria-hidden="true" className="text-slate-300">
          {t("intake_links.detail.breadcrumb_separator")}
        </li>
        <li className="min-w-0 truncate text-slate-700" aria-current="page">
          {projectTitle ? (
            projectId ? (
              <Link
                to={`/projects/${projectId}`}
                className="focus-visible:ring-brand-500 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {projectTitle}
              </Link>
            ) : (
              projectTitle
            )
          ) : (
            <span className="text-slate-400">{t("intake_links.detail.project_deleted")}</span>
          )}
        </li>
      </ol>
    </nav>
  );
}

function LoadingState() {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex items-center justify-center rounded-xl border border-slate-200 bg-white py-12 text-sm text-slate-400">
      <svg
        className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
        <path d="M14 8a6 6 0 0 1-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {t("app.loading")}
    </div>
  );
}

function NotFoundState({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
      <h2 className="text-base font-semibold text-slate-900">
        {t("intake_links.detail.not_found.title")}
      </h2>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        {t("intake_links.detail.not_found.subtitle")}
      </p>
      <button
        type="button"
        onClick={onBack}
        className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 mt-4 touch-manipulation rounded-md px-4 py-2 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("intake_links.detail.not_found.back")}
      </button>
    </div>
  );
}

function EmptyEventsState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center">
      <h3 className="text-base font-semibold text-slate-900">
        {t("intake_links.detail.events.empty.title")}
      </h3>
      <p className="mt-1 max-w-md text-sm text-slate-500">
        {t("intake_links.detail.events.empty.subtitle")}
      </p>
    </div>
  );
}

/**
 * 9.N.3.C — empty state distinct du « aucune soumission reçue ». L'utilisateur
 * a appliqué au moins 1 filtre et la liste est vide → on suggère reset plutôt
 * que d'attendre passivement une soumission. Iconographie amber pour distinguer
 * du EmptyEventsState (slate).
 */
function NoMatchEventsState({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-amber-200 bg-amber-50/40 px-6 py-10 text-center">
      <h3 className="text-base font-semibold text-slate-900">
        {t("intake_links.detail.events.no_match.title")}
      </h3>
      <p className="mt-1 max-w-md text-sm text-slate-600">
        {t("intake_links.detail.events.no_match.subtitle")}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="focus-visible:ring-brand-500 mt-3 inline-flex touch-manipulation items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("intake_links.detail.events.no_match.reset")}
      </button>
    </div>
  );
}

function formatDateShort(iso: string, lang: "fr" | "en"): string {
  try {
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
